/**
 * Client-side quantity + value math for the GRN composer preview.
 *
 * The composer works in operator decimal strings (received / accepted),
 * exactly as the `goodsReceipts.record` wire contract's `recordLineInput`
 * expects (`qtyString` = `/^\d+(\.\d{0,3})?$/`). The preview derives:
 *
 *   - rejected/short per line  = received − accepted (clamped at 0)
 *   - PO fulfilment            = "received" if every line's received ≥ ordered,
 *                                else "partially_received"
 *   - draft matching amount    = Σ acceptedQtyMilli × ratePaise / 1000  (bigint)
 *
 * Quantities round-trip through integer milli-units (qty × 1000) so the
 * preview never touches floats. Money is integer paise (AGENTS.md §7 rule 7).
 *
 * @see packages/api-contract/src/procedures/goods-receipts.ts (recordLineInput)
 */
import type { GrnLineRow } from "./form-schema.js";

/** Parse an operator quantity string (up to 3 decimals) → integer milli-units. */
export function qtyToMilli(value: string): bigint {
  const cleaned = value.trim();
  if (!/^\d+(\.\d{0,3})?$/.test(cleaned)) return 0n;
  const [whole = "0", fraction = ""] = cleaned.split(".");
  return BigInt(whole || "0") * 1000n + BigInt((fraction + "000").slice(0, 3));
}

/** Integer milli-units → display decimal ("5000" / "7.5"), trailing zeros trimmed. */
export function milliToDisplay(milli: bigint | number): string {
  const m = typeof milli === "bigint" ? milli : BigInt(Math.round(milli));
  const whole = m / 1000n;
  const remainder = m % 1000n;
  const wholeStr = whole.toLocaleString("en-IN");
  if (remainder === 0n) return wholeStr;
  return `${wholeStr}.${remainder.toString().padStart(3, "0").replace(/0+$/, "")}`;
}

/** Milli-units → plain operator string ("5000", "7.5") — for default field values. */
export function milliToInput(milli: bigint | number): string {
  const m = typeof milli === "bigint" ? milli : BigInt(Math.round(milli));
  const whole = m / 1000n;
  const remainder = m % 1000n;
  if (remainder === 0n) return whole.toString();
  return `${whole}.${remainder.toString().padStart(3, "0").replace(/0+$/, "")}`;
}

/** Rejected / short for one line — received − accepted, clamped at zero. */
export function rejectedMilli(row: GrnLineRow): bigint {
  const diff = qtyToMilli(row.received) - qtyToMilli(row.accepted);
  return diff > 0n ? diff : 0n;
}

/** Accepted value for one line, in integer paise. */
export function acceptedValuePaise(row: GrnLineRow): bigint {
  return (qtyToMilli(row.accepted) * BigInt(row.ratePaise || "0")) / 1000n;
}

export interface GrnTotals {
  /** Lines with any accepted quantity. */
  acceptedLineCount: number;
  totalLineCount: number;
  /** Σ rejected/short across lines, in milli-units. */
  rejectedMilli: bigint;
  /** Σ accepted × rate, in integer paise — the draft matching amount. */
  matchingPaise: bigint;
  /** PO fulfilment after post: full receipt vs partial. */
  poFulfilment: "received" | "partially_received";
}

export function computeGrnTotals(rows: GrnLineRow[]): GrnTotals {
  let acceptedLineCount = 0;
  let rejected = 0n;
  let matching = 0n;
  let allFullyReceived = rows.length > 0;

  for (const row of rows) {
    const accepted = qtyToMilli(row.accepted);
    if (accepted > 0n) acceptedLineCount += 1;
    rejected += rejectedMilli(row);
    matching += acceptedValuePaise(row);
    if (qtyToMilli(row.received) < qtyToMilli(row.ordered)) allFullyReceived = false;
  }

  return {
    acceptedLineCount,
    totalLineCount: rows.length,
    rejectedMilli: rejected,
    matchingPaise: matching,
    poFulfilment: allFullyReceived ? "received" : "partially_received",
  };
}

/** A line is recordable once it has any received quantity. */
export function hasReceivedQty(row: GrnLineRow): boolean {
  return qtyToMilli(row.received) > 0n;
}
